import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { GiHamburgerMenu } from "react-icons/gi";
import { VscChromeClose } from "react-icons/vsc";
import { FiChevronDown, FiLogOut } from "react-icons/fi";
import AuthContext from "../context/AuthContext";

const navLinks = [
  { label: "Accueil", path: "/" },
  { label: "Nos Services", path: "/#services" },
  { label: "Journées à Thèmes", path: "/journées-a-themes" },
  { label: "Traiteur", path: "/traiteur" },
];

const carteLinks = [
  { label: "Les Entrées",     path: "/entrees" },
  { label: "Les Plats Chauds", path: "/plats-chauds" },
  { label: "Les Menus",       path: "/les-menus" },
  { label: "Les Viandes",     path: "/les-viandes" },
  { label: "Les Fondues",     path: "/les-fondues" },
  { label: "Les Desserts",    path: "/desserts" },
  { label: "Les Vins",        path: "/vins" },
];

export default function Navbar() {
  const [scrolled, setScrolled]       = useState(false);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [carteOpen, setCarteOpen]     = useState(false);
  const [mobileCarteOpen, setMobileCarteOpen] = useState(false);
  const carteRef = useRef(null);
  const { user, logoutUser }          = useContext(AuthContext);
  const navigate                      = useHistory();

  /* scroll listener */
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  /* close carte dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (carteRef.current && !carteRef.current.contains(e.target)) {
        setCarteOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* lock body scroll when mobile menu open */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const go = (path) => {
    setMobileOpen(false);
    setCarteOpen(false);
    if (path.includes("#")) {
      /* lien ancre (ex: /#footer) → naviguer puis scroller vers l'élément */
      const [route, hash] = path.split("#");
      navigate.push(route || "/");
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 120);
    } else {
      /* lien normal → naviguer ET remonter en haut */
      navigate.push(path);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <NavBar $scrolled={scrolled}>
        <Brand onClick={() => go("/")}>
          <BrandName>La Petite Charrue</BrandName>
          <BrandSub>Auberge du Terroir</BrandSub>
        </Brand>

        <NavLinks>
          {navLinks.map((l) => (
            <NavItem key={l.label} onClick={() => go(l.path)}>
              {l.label}
            </NavItem>
          ))}

          {/* Dropdown Carte */}
          <DropdownWrap ref={carteRef}>
            <NavItem
              as="button"
              $active={carteOpen}
              onClick={() => setCarteOpen((v) => !v)}
            >
              Carte <FiChevronDown style={{ transition: "transform 0.25s", transform: carteOpen ? "rotate(180deg)" : "none" }} />
            </NavItem>
            {carteOpen && (
              <DropMenu>
                {carteLinks.map((l) => (
                  <DropItem key={l.label} onClick={() => go(l.path)}>
                    {l.label}
                  </DropItem>
                ))}
              </DropMenu>
            )}
          </DropdownWrap>

          <NavItem onClick={() => go("/#footer")}>Contact</NavItem>

          {user && (
            <LogoutBtn onClick={logoutUser}>
              <FiLogOut /> Déconnexion
            </LogoutBtn>
          )}
        </NavLinks>

        <HamburgerBtn
          onClick={(e) => { e.stopPropagation(); setMobileOpen(true); }}
          aria-label="Ouvrir le menu"
        >
          <GiHamburgerMenu />
        </HamburgerBtn>
      </NavBar>

      {/* Mobile overlay */}
      <Overlay $open={mobileOpen} onClick={() => setMobileOpen(false)} />

      <MobileMenu $open={mobileOpen}>
        <MobileHeader>
          <MobileBrand>La Petite Charrue</MobileBrand>
          <CloseBtn onClick={() => setMobileOpen(false)} aria-label="Fermer">
            <VscChromeClose />
          </CloseBtn>
        </MobileHeader>

        <MobileLinks>
          {navLinks.map((l) => (
            <MobileItem key={l.label} onClick={() => go(l.path)}>
              {l.label}
            </MobileItem>
          ))}

          <MobileCarteToggle onClick={() => setMobileCarteOpen((v) => !v)}>
            Carte{" "}
            <FiChevronDown
              style={{
                transition: "transform 0.25s",
                transform: mobileCarteOpen ? "rotate(180deg)" : "none",
              }}
            />
          </MobileCarteToggle>
          {mobileCarteOpen &&
            carteLinks.map((l) => (
              <MobileSubItem key={l.label} onClick={() => go(l.path)}>
                {l.label}
              </MobileSubItem>
            ))}

          <MobileItem onClick={() => go("/#footer")}>Contact</MobileItem>

          {user && (
            <MobileLogout onClick={logoutUser}>
              <FiLogOut /> Déconnexion
            </MobileLogout>
          )}
        </MobileLinks>

        <MobileFooter>
          <span>03 84 23 89 14</span>
        </MobileFooter>
      </MobileMenu>
    </>
  );
}

/* ─── Styled Components ─── */

const NavBar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5vw;
  height: 72px;
  transition: background 0.4s ease, box-shadow 0.4s ease;

  ${({ $scrolled }) =>
    $scrolled
      ? css`
          background: rgba(10, 6, 4, 0.96);
          backdrop-filter: blur(12px);
          box-shadow: 0 2px 24px rgba(0, 0, 0, 0.6);
          border-bottom: 1px solid rgba(200, 151, 62, 0.15);
        `
      : css`
          background: linear-gradient(to bottom, rgba(0,0,0,0.7), transparent);
        `}
`;

const Brand = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  user-select: none;
`;

const BrandName = styled.span`
  font-family: var(--font-heading);
  font-size: 1.4rem;
  font-weight: 600;
  font-style: italic;
  color: var(--primary);
  letter-spacing: 0.04em;
`;

const BrandSub = styled.span`
  font-family: var(--font-script);
  font-size: 0.8rem;
  color: var(--cream-dim);
  letter-spacing: 0.12em;
`;

const NavLinks = styled.ul`
  display: flex;
  list-style: none;
  align-items: center;
  gap: 0.25rem;

  @media (max-width: 1024px) { display: none; }
`;

const NavItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.45rem 0.85rem;
  color: ${({ $active }) => ($active ? "var(--primary)" : "var(--cream-dim)")};
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: var(--transition);
  background: none;
  border: none;
  font-family: var(--font-body);

  &:hover {
    color: var(--primary);
    background: var(--primary-glow);
  }
`;

const DropdownWrap = styled.div`
  position: relative;
`;

const dropIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const DropMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 200px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: ${dropIn} 0.2s ease;
  z-index: 200;
`;

const DropItem = styled.div`
  padding: 0.7rem 1.25rem;
  font-size: 0.85rem;
  color: var(--cream-dim);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 400;
  letter-spacing: 0.04em;
  border-bottom: 1px solid var(--border-light);

  &:last-child { border-bottom: none; }
  &:hover {
    background: var(--primary-glow);
    color: var(--primary);
    padding-left: 1.6rem;
  }
`;

const LogoutBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 1rem;
  background: transparent;
  border: 1px solid var(--secondary);
  color: var(--secondary-light);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);
  font-family: var(--font-body);

  &:hover {
    background: var(--secondary);
    color: white;
  }
`;

const HamburgerBtn = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--primary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 1024px) { display: flex; align-items: center; }
`;

const Overlay = styled.div`
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  z-index: 1099;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "all" : "none")};
  transition: opacity 0.3s;

  @media (max-width: 1024px) { display: block; }
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 0;
  right: 0;
  width: min(85vw, 320px);
  height: 100dvh;
  background: var(--bg-surface);
  border-left: 1px solid var(--border);
  z-index: 1100;
  flex-direction: column;
  transform: ${({ $open }) => ($open ? "translateX(0)" : "translateX(100%)")};
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;

  @media (max-width: 1024px) { display: flex; }
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const MobileBrand = styled.span`
  font-family: var(--font-heading);
  font-style: italic;
  color: var(--primary);
  font-size: 1.1rem;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: var(--cream-dim);
  font-size: 1.3rem;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  transition: color 0.2s;
  &:hover { color: var(--primary); }
`;

const MobileLinks = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.25rem 0;
  flex: 1;
`;

const MobileItem = styled.div`
  padding: 0.9rem 1.75rem;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--cream-dim);
  cursor: pointer;
  transition: var(--transition);
  border-bottom: 1px solid var(--border-light);

  &:hover {
    color: var(--primary);
    background: var(--primary-glow);
    padding-left: 2.25rem;
  }
`;

const MobileCarteToggle = styled(MobileItem)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 1.75rem;
`;

const MobileSubItem = styled.div`
  padding: 0.7rem 1.75rem 0.7rem 2.5rem;
  font-size: 0.88rem;
  color: var(--cream-muted);
  cursor: pointer;
  transition: var(--transition);
  border-bottom: 1px solid var(--border-light);
  background: rgba(255,255,255,0.02);

  &::before {
    content: "— ";
    color: var(--primary);
    font-size: 0.75rem;
  }

  &:hover { color: var(--primary); background: var(--primary-glow); }
`;

const MobileLogout = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.9rem 1.75rem;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #e05555;
  cursor: pointer;
  transition: var(--transition);
  border-top: 1px solid var(--border);
  margin-top: auto;

  &:hover { background: rgba(139, 26, 26, 0.15); }
`;

const MobileFooter = styled.div`
  padding: 1.25rem 1.75rem;
  border-top: 1px solid var(--border);
  color: var(--cream-muted);
  font-size: 0.85rem;
  letter-spacing: 0.05em;
`;
